import { useTheme } from "@/hooks/useTheme";
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Sun, Moon, Monitor, Maximize, Minimize } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function FullscreenToggle({ hideFullscreen = false }) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  if (hideFullscreen) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={toggleFullscreen}
            variant="ghost"
            size="icon"
            className="w-9 h-9 rounded-full text-foreground hover:text-primary transition-all duration-300"
          >
            {isFullscreen ? (
              <Minimize className="h-5 w-5" />
            ) : (
              <Maximize className="h-5 w-5" />
            )}
            <span className="sr-only">
              {isFullscreen ? "Exit fullscreen" : "Full screen"}
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isFullscreen ? "Exit fullscreen" : "Full screen"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ThemeToggle() {
  const { theme, setTheme, currentTheme, mounted } = useTheme();

  // Return a placeholder during SSR to avoid hydration mismatch
  if (!mounted) {
    return (
      <Button variant="ghost" size="icon" className="w-9 h-9 opacity-0">
        <Sun className="h-5 w-5" />
        <span className="sr-only">Toggle theme</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="w-9 h-9 rounded-full text-foreground hover:text-primary transition-all duration-300"
        >
          {currentTheme === "dark" ? (
            <Moon className="h-5 w-5 animate-fade-in" />
          ) : (
            <Sun className="h-5 w-5 animate-fade-in" />
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="animate-slide-up">
        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className={`flex items-center gap-2 ${
            theme === "light" ? "bg-accent" : ""
          } cursor-pointer`}
        >
          <Sun className="h-4 w-4" />
          <span>Light</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className={`flex items-center gap-2 ${
            theme === "dark" ? "bg-accent" : ""
          } cursor-pointer`}
        >
          <Moon className="h-4 w-4" />
          <span>Dark</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("system")}
          className={`flex items-center gap-2 ${
            theme === "system" ? "bg-accent" : ""
          } cursor-pointer`}
        >
          <Monitor className="h-4 w-4" />
          <span>System</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
